import {
  CHESTNY_ZNAK_LABELS_RU,
  CUSTOMS_EXCLUDED_ITEM_STATUSES,
  CUSTOMS_SENT_TO_LOGIST_LABEL_RU,
  CUSTOMS_STATUS_LABELS_RU,
  PROCUREMENT_VISIBLE_REQUEST_STATUSES,
  RAISED_CUSTOMS_FEE_RUB,
  formatDutyRate,
  isCustomsAnswerStale,
  isCustomsMatrix,
  resolveCustomsZnak,
  type ChestnyZnak,
  type ItemCustomsSummaryDto,
} from '@crm/shared'

/**
 * Нетарифка в карточке заявки (Л1–Л3, docs/features/nontariff.md): ссылка на
 * даташит и итог проверки логиста по позициям из-за рубежа — статус, флаг
 * «внимание», ТН ВЭД, пошлина, сбор, «Честный знак», разрешительные документы.
 *
 * Своё значение позиции — обычным текстом. Нет своего — серым подсказка из
 * памяти по партномеру (прошлые проверки): ТН ВЭД и знак — «из архива от даты»,
 * пошлина, сбор и документы — «на дату», их логист всё равно перепроверит.
 * Позиции не из-за рубежа (РФ, Склад, без матрицы) — прочерк.
 */

type Tone = 'own' | 'archive' | 'danger'

/** Значение ячейки нетарифки: текст, серая приписка и подсказка по наведению. */
export interface CustomsCellValue {
  text: string
  tone: Tone
  /** Серая приписка после значения: «из архива от 01.05.2026». */
  note?: string
  title?: string
}

type Customs = ItemCustomsSummaryDto | null | undefined

/**
 * Показывать нетарифку позиции: сервер с 30.10.2026 присылает shown (матрица из-за
 * рубежа, статус заявки не важен — в «Отказе» тоже видно); старый ответ — eligible.
 */
export function isCustomsShown(customs: Customs): customs is ItemCustomsSummaryDto {
  return !!(customs?.shown ?? customs?.eligible)
}

/** «2026-05-01» → «01.05.2026»; ISO со временем — по местной дате. Пусто → ''. */
export function formatCustomsDate(value: string | null | undefined): string {
  if (!value) return ''
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (day) return `${day[3]}.${day[2]}.${day[1]}`
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU')
}

function formatFeeRub(amount: number): string {
  return `${Math.round(amount).toLocaleString('ru-RU')} ₽`
}

/** Приписка к архивному значению, если прошлой проверке больше месяца. */
export const STALE_NOTE = 'старше месяца, перепроверить'

function staleSuffix(customs: Customs): string {
  return isCustomsAnswerStale(customs?.hint?.lastCheckedAt) ? ` · ${STALE_NOTE}` : ''
}

function archiveNote(customs: Customs): string {
  const date = formatCustomsDate(customs?.hint?.lastCheckedAt)
  return (date ? `из архива от ${date}` : 'из архива') + staleSuffix(customs)
}

function onDateNote(customs: Customs): string {
  const date = formatCustomsDate(customs?.hint?.lastCheckedAt)
  return (date ? `на ${date}` : 'из архива') + staleSuffix(customs)
}

/**
 * Ответ брокера по позиции старше месяца (решение Сергея 19.10.2026): данные
 * остаются для справки, но перед закупкой их перепроверяют — позицию отправляют
 * логисту заново. Подсказка по наведению; не устарел — null.
 */
export function customsStaleTitle(customs: Customs): string | null {
  if (!isCustomsShown(customs) || customs?.status !== 'ANSWERED') return null
  const at = customs?.check?.checkedAt
  if (!isCustomsAnswerStale(at)) return null
  return `Ответ от ${formatCustomsDate(at)} — больше месяца назад. Данные для справки: перед закупкой перепроверьте — отправьте позицию логисту заново`
}

const RECHECK_TITLE = 'Из прошлой проверки этого партномера — справочно, логист перепроверит'

/**
 * Статус нетарифки позиции в карточке заявки. «В очереди» у логиста — для
 * продаж «Отправлено логисту»: позицию отправил менеджер, логист её ещё не брал.
 */
export function customsStatusLabel(customs: Customs): string {
  if (!isCustomsShown(customs) || !customs?.status) return ''
  if (customs.status === 'PENDING') return CUSTOMS_SENT_TO_LOGIST_LABEL_RU
  return CUSTOMS_STATUS_LABELS_RU[customs.status] ?? customs.status
}

export function customsTnvedCell(customs: Customs): CustomsCellValue | null {
  if (!isCustomsShown(customs)) return null
  const own = customs.check?.tnvedCode
  if (own) return { text: own, tone: 'own' }
  const fromHint = customs.hint?.tnvedCode
  if (fromHint)
    return {
      text: fromHint,
      tone: 'archive',
      note: archiveNote(customs),
      title: 'Код ТН ВЭД из прошлой проверки этого партномера',
    }
  return null
}

export function customsDutyCell(customs: Customs): CustomsCellValue | null {
  if (!isCustomsShown(customs)) return null
  const own = customs.check?.dutyRate
  if (own != null) return { text: formatDutyRate(own), tone: 'own' }
  const last = customs.hint?.lastDutyRate
  if (last != null)
    return {
      text: formatDutyRate(last),
      tone: 'archive',
      note: onDateNote(customs),
      title: RECHECK_TITLE,
    }
  return null
}

export function customsFeeCell(customs: Customs): CustomsCellValue | null {
  if (!isCustomsShown(customs)) return null
  const raised = customs.check?.customsFeeRaised
  if (raised === true) {
    const amount = customs.check?.customsFeeAmount ?? RAISED_CUSTOMS_FEE_RUB
    return {
      text: `Повышенный ${formatFeeRub(amount)}`,
      tone: 'danger',
      title: 'Повышенный таможенный сбор — берётся на декларацию',
    }
  }
  if (raised === false) return { text: '0', tone: 'own' }
  const last = customs.hint?.lastFeeRaised
  if (last == null) return null
  return {
    text: last
      ? `Повышенный ${formatFeeRub(customs.hint?.lastFeeAmount ?? RAISED_CUSTOMS_FEE_RUB)}`
      : '0',
    tone: 'archive',
    note: onDateNote(customs),
    title: RECHECK_TITLE,
  }
}

function znakText(value: ChestnyZnak, from: string | null): string {
  if (value === 'YES') {
    const date = formatCustomsDate(from)
    return date ? `${CHESTNY_ZNAK_LABELS_RU.YES} с ${date}` : CHESTNY_ZNAK_LABELS_RU.YES
  }
  return CHESTNY_ZNAK_LABELS_RU[value]
}

/**
 * «Честный знак» позиции — общее правило shared (resolveCustomsZnak): своё значение,
 * в т.ч. сохранённое логистом «Неизвестно»; память — только если своего ответа нет
 * или она свежее.
 */
export function customsZnakCell(customs: Customs): CustomsCellValue | null {
  if (!isCustomsShown(customs)) return null
  const z = resolveCustomsZnak(customs.check, customs.hint)
  if (!z) return null
  if (!z.fromArchive)
    return { text: znakText(z.value, z.from), tone: z.value === 'YES' ? 'danger' : 'own' }
  return {
    text: znakText(z.value, z.from),
    tone: 'archive',
    note: archiveNote(customs),
    title: '«Честный знак» из прошлой проверки этого партномера',
  }
}

/**
 * Значение фильтра колонки «Честный знак»: ровно три варианта — «Нужен», «Не нужен»,
 * «Неизвестно» (свой ответ и архив вместе, без дат). Позиции не из-за рубежа — пусто.
 */
export function customsZnakFilterValue(customs: Customs): string {
  if (!isCustomsShown(customs)) return ''
  return CHESTNY_ZNAK_LABELS_RU[resolveCustomsZnak(customs.check, customs.hint)?.value ?? 'UNKNOWN']
}

export function customsDocsCell(customs: Customs): CustomsCellValue | null {
  if (!isCustomsShown(customs)) return null
  const own = customs.check?.nonTariffNote?.trim()
  if (own) return { text: own, tone: 'own', title: own }
  const last = customs.hint?.lastNonTariffNote?.trim()
  if (last)
    return {
      text: last,
      tone: 'archive',
      note: onDateNote(customs),
      title: `${onDateNote(customs)}: ${last}`,
    }
  return null
}

/** «Сертификат» из ответа брокера — только свой ответ позиции (в памяти по партномеру его нет). */
export function customsCertificateCell(customs: Customs): CustomsCellValue | null {
  if (!isCustomsShown(customs)) return null
  const own = customs?.check?.certificateNote?.trim()
  return own ? { text: own, tone: 'own', title: own } : null
}

/** «Особенности» из ответа брокера — только свой ответ позиции. */
export function customsFeaturesCell(customs: Customs): CustomsCellValue | null {
  if (!isCustomsShown(customs)) return null
  const own = customs?.check?.featuresNote?.trim()
  return own ? { text: own, tone: 'own', title: own } : null
}

/** «Внимание» — причины красного флага (повышенный сбор, «Честный знак», документы…). */
export function customsAttentionCell(customs: Customs): CustomsCellValue | null {
  if (!isCustomsShown(customs)) return null
  const reasons = customs?.check?.attentionReasons ?? []
  if (reasons.length === 0) return null
  const text = reasons.join(', ')
  return { text, tone: 'danger', title: `Внимание: ${text}` }
}

/** Плоский текст ячейки — для фильтра-воронки и копирования в Excel. */
export function flat(cell: CustomsCellValue | null): string {
  if (!cell) return ''
  return cell.note ? `${cell.text} (${cell.note})` : cell.text
}

/**
 * Матрицу позиции меняют прямо в таблице (кеш патчится без перечитывания), а
 * `customs.eligible` посчитан сервером по старой матрице. Сверяем по текущей:
 * стала из-за рубежа — статус записи, если позицию отправляли логисту (иначе прочерк),
 * стала «РФ»/«Склад» — прочерк.
 */
function reconcileEligibility(
  customs: ItemCustomsSummaryDto | null,
  matrix: string | null | undefined,
  itemStatus: string | undefined,
  requestStatus: string | undefined,
): ItemCustomsSummaryDto | null {
  if (!customs || matrix === undefined) return customs
  // Правило «можно отправить логисту», как на сервере (isCustomsEligibleItem): матрица из-за рубежа,
  // позиция не отменена/не неактуальна, заявка видна закупке. Без статусов позиции и
  // заявки «вне очереди» сервера не перебиваем: причина могла быть не в матрице.
  const statusesKnown = itemStatus !== undefined && requestStatus !== undefined
  const eligible =
    isCustomsMatrix(matrix) &&
    (itemStatus === undefined ||
      !(CUSTOMS_EXCLUDED_ITEM_STATUSES as readonly string[]).includes(itemStatus)) &&
    (requestStatus === undefined ||
      (PROCUREMENT_VISIBLE_REQUEST_STATUSES as string[]).includes(requestStatus))
  // Показ нетарифки зависит только от матрицы (с 30.10.2026 — и в заявках «Отказ»).
  const shown = isCustomsMatrix(matrix)
  if (eligible === customs.eligible && shown === isCustomsShown(customs)) return customs
  if (eligible && !statusesKnown && shown === isCustomsShown(customs)) return customs
  const check = customs.check
  const sentToLogist = eligible && !!check?.sentToLogistAt
  return {
    ...customs,
    eligible,
    shown,
    sentToLogist,
    status: shown && check?.sentToLogistAt ? check.status : null,
  }
}

/**
 * Поля строки сетки для даташита и нетарифки. Фильтр-воронка читает значение по
 * `field`, поэтому у каждой колонки — свой плоский текст; объект `customs`
 * рендерерам нужен целиком (цвет, приписки, причины флага).
 */
export function customsRowFields(
  item: {
    matrix?: string | null
    status?: string
    datasheetUrl?: string | null
    customs?: ItemCustomsSummaryDto | null
  },
  /** Статус заявки — для полного правила очереди при смене матрицы в таблице. */
  requestStatus?: string,
): Record<string, unknown> {
  const customs = reconcileEligibility(
    item.customs ?? null,
    item.matrix,
    item.status,
    requestStatus,
  )
  return {
    datasheetUrl: item.datasheetUrl ?? null,
    customs,
    // «· старше месяца» — чтобы фильтром найти устаревшие ответы.
    customsStatusLabel:
      customsStatusLabel(customs) + (customsStaleTitle(customs) ? ' · старше месяца' : ''),
    customsTnved: flat(customsTnvedCell(customs)),
    customsDuty: flat(customsDutyCell(customs)),
    customsFee: flat(customsFeeCell(customs)),
    customsZnak: customsZnakFilterValue(customs),
    customsDocs: flat(customsDocsCell(customs)),
    customsCertificate: flat(customsCertificateCell(customs)),
    customsFeatures: flat(customsFeaturesCell(customs)),
    customsAttention: flat(customsAttentionCell(customs)),
  }
}

const TONE_CLASS: Record<Tone, string> = {
  own: 'text-gray-800',
  archive: 'text-gray-400',
  danger: 'text-red-600 font-medium',
}

export function CustomsValue({
  cell,
  wrap = false,
}: {
  cell: CustomsCellValue | null
  wrap?: boolean
}) {
  if (!cell) return <span className="text-gray-400">—</span>
  return (
    <span
      className={`text-xs ${TONE_CLASS[cell.tone]} ${wrap ? 'block whitespace-normal break-words leading-snug py-1.5' : 'whitespace-nowrap'}`}
      title={cell.title}
    >
      {cell.text}
      {cell.note && <span className="ml-1 text-[10px] text-gray-400">{cell.note}</span>}
    </span>
  )
}
